// Projects + Timesheet — basic UI (iskelet).
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader, EmptyState } from '../components/ui';
import { toast } from '../components/Toast';
import { fmtAmount } from '../utils/numberFormat';
import CustomFieldsSection from '../components/CustomFieldsSection';

const STATUSES = [
  { v: 'active',    l: 'Aktif',     c: '#10b981' },
  { v: 'paused',    l: 'Bekleme',   c: '#f59e0b' },
  { v: 'completed', l: 'Tamamlandı', c: '#3b82f6' },
  { v: 'archived',  l: 'Arşivli',   c: '#64748b' },
];

const TASK_STATUSES = [
  { v: 'todo',        l: 'Yapılacak',  c: '#94a3b8' },
  { v: 'in_progress', l: 'Devam Ediyor', c: '#3b82f6' },
  { v: 'done',        l: 'Tamam',      c: '#10b981' },
  { v: 'blocked',     l: 'Engellendi', c: '#dc2626' },
];

export default function ProjectsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('info');
  const [tasks, setTasks] = useState([]);
  const [entries, setEntries] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', budget_amount: '', budget_hours: '', status: 'active' });
  const [newTask, setNewTask] = useState({ name: '', estimated_hours: '' });
  const [newEntry, setNewEntry] = useState({ entry_date: new Date().toISOString().slice(0, 10), hours: '', description: '' });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/projects', { params: { search } });
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { toast.error('Liste yüklenemedi'); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openProject = async (p) => {
    setSelected(p);
    setTab('info');
    try {
      const [tR, eR] = await Promise.all([
        api.get('/projects/' + p.id + '/tasks'),
        api.get('/projects/timesheet/entries', { params: { project_id: p.id } }),
      ]);
      setTasks(tR.data?.data || []);
      setEntries(eR.data?.data || []);
    } catch (e) {}
  };

  const createProject = async () => {
    if (!form.name.trim()) { toast.error('Ad zorunlu'); return; }
    try {
      await api.post('/projects', form);
      toast.success('Proje oluşturuldu');
      setShowNew(false);
      setForm({ name: '', budget_amount: '', budget_hours: '', status: 'active' });
      fetchList();
    } catch (e) { toast.error('Hata'); }
  };

  const addTask = async () => {
    if (!newTask.name.trim()) { toast.error('Görev adı zorunlu'); return; }
    try {
      await api.post('/projects/' + selected.id + '/tasks', newTask);
      setNewTask({ name: '', estimated_hours: '' });
      const r = await api.get('/projects/' + selected.id + '/tasks');
      setTasks(r.data?.data || []);
    } catch (e) { toast.error('Eklenemedi'); }
  };

  const updateTaskStatus = async (tid, status) => {
    try {
      await api.patch('/projects/tasks/' + tid, { status });
      setTasks(prev => prev.map(t => t.id === tid ? { ...t, status } : t));
    } catch (e) {}
  };

  const addEntry = async () => {
    if (!newEntry.hours) { toast.error('Saat zorunlu'); return; }
    try {
      await api.post('/projects/timesheet/entries', { ...newEntry, project_id: selected.id });
      setNewEntry({ entry_date: new Date().toISOString().slice(0, 10), hours: '', description: '' });
      const r = await api.get('/projects/timesheet/entries', { params: { project_id: selected.id } });
      setEntries(r.data?.data || []);
      fetchList();
      toast.success('Saat kaydedildi');
    } catch (e) { toast.error('Hata'); }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Projeler"
        unit="proje"
        count={list.length}
        searchValue={search}
        onSearch={setSearch}
        searchPlaceholder="Proje adı, kod..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={() => setShowNew(true)}>+ Yeni Proje</Button>}
      />

      {!loading && list.length === 0 ? (
        <EmptyState icon="📋" title="Henüz proje yok" description='"+ Yeni Proje" ile başlayın.' />
      ) : (
        <div style={{ padding: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
            {list.map(p => {
              const st = STATUSES.find(s => s.v === p.status) || STATUSES[0];
              const budgetPct = p.budget_amount && p.spent_amount ? Math.min((p.spent_amount / p.budget_amount) * 100, 100) : 0;
              return (
                <div key={p.id} style={S.card} onClick={() => openProject(p)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <strong style={{ fontSize: 'var(--text-sm)' }}>{p.name}</strong>
                    <span style={{ ...S.statusBadge, background: st.c + '22', color: st.c }}>{st.l}</span>
                  </div>
                  {p.account_title && <div style={S.sub}>👤 {p.account_title}</div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', marginTop: 8 }}>
                    <span>Saat: <strong>{Math.round(p.spent_hours || 0)}</strong>{p.budget_hours ? ' / ' + p.budget_hours : ''}</span>
                    {p.budget_amount && (
                      <span>Maliyet: <strong>{fmtAmount(p.spent_amount || 0)}</strong> / {fmtAmount(p.budget_amount)}</span>
                    )}
                  </div>
                  {p.budget_amount && (
                    <div style={{ height: 4, background: 'var(--bg-tertiary)', borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: budgetPct + '%', background: budgetPct > 90 ? 'var(--danger)' : 'var(--accent-primary)', transition: 'width 0.3s' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NEW PROJECT */}
      {showNew && (
        <Modal title="Yeni Proje" size="md" onClose={() => setShowNew(false)}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setShowNew(false)}>İptal</Button>
              <Button onClick={createProject}>💾 Kaydet</Button>
            </div>
          }>
          <div className="form-grid">
            <FormField label="Proje Adı" required>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </FormField>
            <FormField label="Kod">
              <input value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} />
            </FormField>
            <FormField label="Bütçe (₺)">
              <input type="number" value={form.budget_amount} onChange={e => setForm({ ...form, budget_amount: e.target.value })} />
            </FormField>
            <FormField label="Tahmini Saat">
              <input type="number" value={form.budget_hours} onChange={e => setForm({ ...form, budget_hours: e.target.value })} />
            </FormField>
            <FormField label="Başlangıç">
              <input type="date" value={form.start_date || ''} onChange={e => setForm({ ...form, start_date: e.target.value })} />
            </FormField>
            <FormField label="Bitiş">
              <input type="date" value={form.end_date || ''} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </FormField>
          </div>
        </Modal>
      )}

      {/* PROJECT DETAIL */}
      {selected && (
        <Modal title={selected.name} size="xl" onClose={() => setSelected(null)}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setSelected(null)}>Kapat</Button>
            </div>
          }>
          <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border-secondary)' }}>
            {[
              { k: 'info', l: 'Bilgiler' },
              { k: 'tasks', l: 'Görevler (' + tasks.length + ')' },
              { k: 'timesheet', l: 'Saatler (' + entries.length + ')' },
              { k: 'custom', l: 'Özel' },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                style={{
                  padding: '8px 14px', border: 'none', background: 'transparent',
                  color: tab === t.k ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  fontWeight: tab === t.k ? 700 : 500, cursor: 'pointer',
                  borderBottom: '2px solid ' + (tab === t.k ? 'var(--accent-primary)' : 'transparent'),
                  marginBottom: -1,
                }}>{t.l}</button>
            ))}
          </div>

          {tab === 'info' && (
            <div className="form-grid">
              <div><strong>Kod:</strong> {selected.code || '—'}</div>
              <div><strong>Durum:</strong> {STATUSES.find(s => s.v === selected.status)?.l || selected.status}</div>
              <div><strong>Bütçe:</strong> {fmtAmount(selected.budget_amount)} ₺</div>
              <div><strong>Saat hedef:</strong> {selected.budget_hours || '—'}</div>
              <div><strong>Harcanan:</strong> {fmtAmount(selected.spent_amount || 0)} ₺ ({Math.round(selected.spent_hours || 0)} sa)</div>
              <div><strong>Müşteri:</strong> {selected.account_title || '—'}</div>
              {selected.description && (
                <div style={{ gridColumn: '1/-1' }}><strong>Açıklama:</strong> {selected.description}</div>
              )}
            </div>
          )}

          {tab === 'tasks' && (
            <div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <input value={newTask.name} onChange={e => setNewTask({ ...newTask, name: e.target.value })}
                       placeholder="Görev adı" style={{ flex: 1 }} />
                <input type="number" value={newTask.estimated_hours} onChange={e => setNewTask({ ...newTask, estimated_hours: e.target.value })}
                       placeholder="Saat" style={{ width: 80 }} />
                <Button size="sm" onClick={addTask}>+ Ekle</Button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tasks.map(t => {
                  const st = TASK_STATUSES.find(s => s.v === t.status) || TASK_STATUSES[0];
                  return (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-md)' }}>
                      <select value={t.status} onChange={e => updateTaskStatus(t.id, e.target.value)} style={{ minWidth: 130 }}>
                        {TASK_STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                      </select>
                      <span style={{ flex: 1, fontWeight: t.status === 'done' ? 400 : 600, textDecoration: t.status === 'done' ? 'line-through' : 'none', opacity: t.status === 'done' ? 0.6 : 1 }}>{t.name}</span>
                      {t.estimated_hours && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{t.estimated_hours} sa</span>}
                      {t.assignee_name && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--accent-primary)' }}>👤 {t.assignee_name}</span>}
                    </div>
                  );
                })}
                {!tasks.length && <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>Henüz görev yok</div>}
              </div>
            </div>
          )}

          {tab === 'timesheet' && (
            <div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                <input type="date" value={newEntry.entry_date} onChange={e => setNewEntry({ ...newEntry, entry_date: e.target.value })} style={{ width: 140 }} />
                <input type="number" step="0.25" value={newEntry.hours} onChange={e => setNewEntry({ ...newEntry, hours: e.target.value })}
                       placeholder="Saat" style={{ width: 80 }} />
                <input value={newEntry.description} onChange={e => setNewEntry({ ...newEntry, description: e.target.value })}
                       placeholder="Açıklama" style={{ flex: 1, minWidth: 200 }} />
                <Button size="sm" onClick={addEntry}>+ Kaydet</Button>
              </div>
              <table style={{ width: '100%', fontSize: 'var(--text-sm)' }}>
                <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Tarih</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Kullanıcı</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Saat</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Açıklama</th>
                </tr></thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                      <td style={{ padding: 8 }}>{new Date(e.entry_date).toLocaleDateString('tr-TR')}</td>
                      <td style={{ padding: 8 }}>{e.user_name || '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{e.hours}</td>
                      <td style={{ padding: 8 }}>{e.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!entries.length && <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>Saat kaydı yok</div>}
            </div>
          )}

          {tab === 'custom' && (
            <CustomFieldsSection entityType="project" entityId={selected.id} autoSave />
          )}
        </Modal>
      )}
    </div>
  );
}

const S = {
  card: {
    padding: 12,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-xs)',
  },
  statusBadge: {
    fontSize: '0.65rem', fontWeight: 700,
    padding: '2px 8px', borderRadius: 'var(--r-full)',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  sub: { fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 2 },
};
